import axios, {AxiosError, AxiosInstance} from 'axios'
import {
  QueryClientConfig,
  QueryClient,
  useMutation,
  useIsMutating,
  MutationKey,
  MutationFunction,
  UseMutationOptions,
  QueryKey,
} from 'react-query'

import {logout, Session} from 'modules/auth'
import {showErrorNotification} from 'common/helpers'

export const LONG_STALE_QUERY_TIME = 30000

const defaultQueryStaleTime = 1000

const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: defaultQueryStaleTime,
    },
  },
}

export const queryClient = new QueryClient(queryClientConfig)

export const auth: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_URL_BASE_RBAC}/api`,
})

export const api: AxiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}/api`,
})

export const addAuthorizationHeader = (session: Session) => {
  auth.defaults.headers.common.Authorization = `Bearer ${session.access?.token}`
  api.defaults.headers.common.Authorization = `Bearer ${session.access?.token}`
}

export const handleError = (error: AxiosError<any>) => {
  let message = error.message
  if (error.response) {
    message = error.response.data?.errors
      ? Object.values(error.response.data.errors).flat().join('\n')
      : error.response.data?.message || error.message
  }
  showErrorNotification(message)
  return Promise.reject(error)
}

auth.interceptors.response.use(undefined, (error) => {
  if (
    error.response?.request?.responseURL.endsWith('logout') ||
    error?.message === 'canceled'
  )
    return

  if (error.response?.status === 401) {
    logout()
    return
  }
  return handleError(error)
})

api.interceptors.response.use(undefined, (error) => {
  if (
    error.response?.request?.responseURL.endsWith('logout') ||
    error?.message === 'canceled'
  )
    return

  if (error.response?.status === 401) {
    logout()
    return
  }
  return handleError(error)
})

export const useMutationQuery = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  mutationKey: MutationKey,
  mutationFn: MutationFunction<TData, TVariables>,
  options: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationKey' | 'mutationFn'
  > = {}
) => ({
  ...useMutation(mutationKey, mutationFn, options),
  isMutating: Boolean(useIsMutating(mutationKey)),
})

export const replaceQueryClientDataItem = (requestKey: QueryKey, data: any) => {
  queryClient.setQueriesData(requestKey, (oldData: any[]) => {
    const newData = oldData.slice()
    newData[newData.findIndex((item) => item.uuid === data.uuid)] = data
    return newData
  })
}
